import { X } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Button } from './button';
import { ErrorBox } from './feedback';
import { Field, Textarea } from './form';

/** Modaler Dialog auf Basis von <dialog> (Fokusfalle und Escape vom Browser). */
export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      if (typeof d.showModal === 'function') d.showModal();
      else d.setAttribute('open', '');
    } else if (!open && d.open) {
      if (typeof d.close === 'function') d.close();
      else d.removeAttribute('open');
    }
  }, [open]);

  if (!open) return <dialog ref={ref} className="hidden" />;

  const body = (
    <>
      <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        <button type="button" onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Schließen">
          <X className="size-4" />
        </button>
      </div>
      <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4">{children}</div>
      {footer && <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">{footer}</div>}
    </>
  );

  return (
    <dialog
      ref={ref}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      className={cn(
        'fixed inset-0 m-auto w-[calc(100%-2rem)] rounded-lg border border-slate-200 bg-white p-0 shadow-xl backdrop:bg-slate-900/40',
        size === 'sm' && 'max-w-md',
        size === 'md' && 'max-w-xl',
        size === 'lg' && 'max-w-3xl',
      )}
    >
      {onSubmit ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(e);
          }}
        >
          {body}
        </form>
      ) : (
        body
      )}
    </dialog>
  );
}

/** Bestätigung, optional mit Pflicht-Begründung. */
export function ConfirmDialog({
  open,
  onClose,
  title,
  message,
  confirmLabel = 'Bestätigen',
  danger,
  requireReason,
  reasonLabel = 'Begründung',
  loading,
  error,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  requireReason?: boolean;
  reasonLabel?: string;
  loading?: boolean;
  error?: unknown;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      onSubmit={() => onConfirm(reason.trim())}
      footer={
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant={danger ? 'danger' : 'primary'} loading={loading} disabled={requireReason && !reason.trim()}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-sm text-slate-700">{message}</div>
      {requireReason && (
        <Field label={reasonLabel} required>
          {(p) => <Textarea {...p} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />}
        </Field>
      )}
      <ErrorBox error={error} />
    </Dialog>
  );
}
